import React, { useState, useRef } from 'react';
import { X, Move, Trash2, RotateCw, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { decorationLibrary } from '../data/decorationLibrary';

const DecorationEditor = ({ placedDecorations, onDecorationsChange }) => {
  const [selectedCategory, setSelectedCategory] = useState('christmas');
  const [selectedDecoration, setSelectedDecoration] = useState(null);
  const previewRef = useRef(null);

  const handleDragStart = (e, decoration) => {
    e.dataTransfer.setData('decoration', JSON.stringify(decoration));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const rect = previewRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    try {
      const decoration = JSON.parse(e.dataTransfer.getData('decoration'));
      const newDecoration = {
        ...decoration,
        instanceId: `${decoration.id}_${Date.now()}`,
        position: { x, y },
        size: 32,
        rotation: 0,
        opacity: 1
      };

      onDecorationsChange([...placedDecorations, newDecoration]);
    } catch (error) {
      console.error('Error placing decoration:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const updateDecoration = (instanceId, updates) => {
    const updated = placedDecorations.map(dec =>
      dec.instanceId === instanceId ? { ...dec, ...updates } : dec
    );
    onDecorationsChange(updated);
  };

  const removeDecoration = (instanceId) => {
    onDecorationsChange(placedDecorations.filter(dec => dec.instanceId !== instanceId));
  };

  const categories = Object.entries(decorationLibrary);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Left: Decoration Library */}
      <div className="lg:col-span-1 space-y-4">
        <div>
          <Label className="text-sm font-medium mb-2 block">Kategorie wählen:</Label>
          <div className="grid grid-cols-2 gap-2">
            {categories.map(([key, cat]) => (
              <Button
                key={key}
                variant={selectedCategory === key ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(key)}
                className="text-sm"
              >
                {cat.icon} {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Decoration Items */}
        <div>
          <Label className="text-sm font-medium mb-2 block">
            Dekorationen (Drag & Drop):
          </Label>
          <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto p-2 bg-gray-50 rounded-lg">
            {decorationLibrary[selectedCategory]?.items.map((decoration) => (
              <div
                key={decoration.id}
                draggable
                onDragStart={(e) => handleDragStart(e, decoration)}
                className="flex flex-col items-center justify-center p-3 bg-white rounded border-2 border-gray-200 hover:border-purple-400 cursor-move transition-all hover:shadow-md"
                title={decoration.name}
              >
                <span className="text-3xl mb-1">{decoration.emoji}</span>
                <span className="text-xs text-gray-600 text-center">{decoration.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Decoration Properties */}
        {selectedDecoration && (
          <div className="p-4 bg-white rounded-lg border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">Eigenschaften</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDecoration(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Größe: {selectedDecoration.size}px</Label>
                <Input
                  type="range"
                  min="16"
                  max="128"
                  value={selectedDecoration.size}
                  onChange={(e) => updateDecoration(selectedDecoration.instanceId, {
                    size: parseInt(e.target.value)
                  })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="text-xs">Rotation: {selectedDecoration.rotation}°</Label>
                <Input
                  type="range"
                  min="0"
                  max="360"
                  value={selectedDecoration.rotation}
                  onChange={(e) => updateDecoration(selectedDecoration.instanceId, {
                    rotation: parseInt(e.target.value)
                  })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="text-xs">Deckkraft: {Math.round(selectedDecoration.opacity * 100)}%</Label>
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={selectedDecoration.opacity * 100}
                  onChange={(e) => updateDecoration(selectedDecoration.instanceId, {
                    opacity: parseInt(e.target.value) / 100
                  })}
                  className="w-full"
                />
              </div>

              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={() => {
                  removeDecoration(selectedDecoration.instanceId);
                  setSelectedDecoration(null);
                }}
              >
                <Trash2 className="h-3 w-3 mr-2" />
                Löschen
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Right: Live Preview */}
      <div className="lg:col-span-2">
        <Label className="text-sm font-medium mb-2 block">Live-Vorschau (Drag & Drop hier):</Label>
        <div
          ref={previewRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="relative w-full h-[600px] bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden"
          style={{ backgroundImage: 'radial-gradient(circle, #e5e5e5 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        >
          {placedDecorations.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 pointer-events-none">
              <div className="text-center">
                <Move className="h-12 w-12 mx-auto mb-2" />
                <p className="text-sm">Ziehen Sie Dekorationen hierher</p>
              </div>
            </div>
          )}

          {placedDecorations.map((decoration) => (
            <div
              key={decoration.instanceId}
              onClick={() => setSelectedDecoration(decoration)}
              className={`absolute cursor-move hover:ring-2 hover:ring-purple-400 rounded transition-all ${
                selectedDecoration?.instanceId === decoration.instanceId ? 'ring-2 ring-purple-500' : ''
              }`}
              style={{
                left: `${decoration.position.x}%`,
                top: `${decoration.position.y}%`,
                fontSize: `${decoration.size}px`,
                transform: `translate(-50%, -50%) rotate(${decoration.rotation}deg)`,
                opacity: decoration.opacity,
                userSelect: 'none'
              }}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('move', decoration.instanceId);
              }}
              onDragEnd={(e) => {
                const rect = previewRef.current.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                updateDecoration(decoration.instanceId, {
                  position: { x, y }
                });
              }}
            >
              {decoration.emoji}
            </div>
          ))}

          {/* Info Badge */}
          <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm text-xs text-gray-600">
            {placedDecorations.length} Dekoration{placedDecorations.length !== 1 ? 'en' : ''} platziert
          </div>
        </div>

        {placedDecorations.length > 0 && (
          <Button
            variant="outline"
            className="mt-2 w-full"
            onClick={() => {
              if (confirm('Möchten Sie wirklich alle Dekorationen entfernen?')) {
                onDecorationsChange([]);
                setSelectedDecoration(null);
              }
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Alle Dekorationen entfernen
          </Button>
        )}
      </div>
    </div>
  );
};

export default DecorationEditor;
